import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Mail, Lock, User, Phone, MapPin, MailOpen, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { apiSendVerification, apiRegisterVerified } from "../lib/api";
import { useAuthStore } from "../store/authStore";

export default function Register() {
  const nav = useNavigate();
  const { setSession } = useAuthStore();
  const [step, setStep] = useState("form");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const sendCode = async () => {
    if (!name || !email || !password) { toast.error("Nom, email et mot de passe sont obligatoires"); return; }
    if (!isValidEmail(email)) { toast.error("Adresse email invalide"); return; }
    if (password.length < 6) { toast.error("Le mot de passe doit contenir au moins 6 caractères"); return; }
    setBusy(true);
    try {
      const data = await apiSendVerification(email.toLowerCase().trim(), name.trim());
      setStep("verify");
      if (data.dev_code) toast.success(`Code envoyé à ${email} · Code de test : ${data.dev_code}`, { duration: 8000 });
      else toast.success(`Code de vérification envoyé à ${email}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossible d'envoyer le code"); }
    finally { setBusy(false); }
  };

  const verifyAndRegister = async () => {
    if (!code || code.length !== 6) { toast.error("Entrez le code à 6 chiffres"); return; }
    setBusy(true);
    try {
      const data = await apiRegisterVerified({
        email: email.toLowerCase().trim(), name: name.trim(), password, role: "client",
        phone: phone.trim() || null, address: address.trim() || null, verification_code: code,
      });
      setSession(data.access_token, data.user);
      toast.success("Votre compte a été créé avec succès !");
      nav("/", { replace: true });
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossible de créer le compte"); }
    finally { setBusy(false); }
  };

  const field = (Icon, props) => (
    <div className="flex items-center gap-3 px-4 rounded-xl py-3 mb-3" style={{ background: "#f8f9fa", border: "1px solid #e9ecef" }}>
      <Icon size={20} color="#0b1220" />
      <input {...props} className="flex-1 bg-transparent outline-none text-[15px]" style={{ color: "#333" }} />
    </div>
  );

  return (
    <div className="min-h-screen px-4 py-10" style={{ background: "#fff" }}>
      <div className="max-w-md mx-auto">
        <button onClick={() => (step === "verify" ? setStep("form") : nav(-1))} className="mb-4" style={{ color: "#0b1220" }} data-testid="register-back">
          <ArrowLeft size={24} />
        </button>
        <h1 className="text-2xl font-extrabold" style={{ color: "#1a1a1a" }}>{step === "form" ? "Créer un compte" : "Vérification email"}</h1>
        <p className="text-sm mt-1 mb-6" style={{ color: "#666" }}>{step === "form" ? "Rejoignez La Broye Apéro Service" : `Code envoyé à ${email}`}</p>

        {step === "form" ? (
          <>
            {field(User, { placeholder: "Nom complet *", value: name, onChange: (e) => setName(e.target.value), "data-testid": "register-name" })}
            {field(Mail, { placeholder: "Email *", type: "email", value: email, onChange: (e) => setEmail(e.target.value), "data-testid": "register-email" })}
            {field(Lock, { placeholder: "Mot de passe * (min. 6)", type: "password", value: password, onChange: (e) => setPassword(e.target.value), "data-testid": "register-password" })}
            {field(Phone, { placeholder: "Téléphone", value: phone, onChange: (e) => setPhone(e.target.value), "data-testid": "register-phone" })}
            {field(MapPin, { placeholder: "Adresse de livraison", value: address, onChange: (e) => setAddress(e.target.value), "data-testid": "register-address" })}
            <button onClick={sendCode} disabled={busy} data-testid="register-send-code"
              className="w-full rounded-2xl flex items-center justify-center gap-2 font-bold text-white py-3.5 mt-2" style={{ background: "linear-gradient(90deg,#0b1220,#333)" }}>
              <Mail size={20} /> {busy ? "Envoi en cours..." : "Vérifier mon email"}
            </button>
            <Link to="/login" className="block text-center text-sm mt-5" style={{ color: "#666" }}>Déjà un compte ? <span className="font-bold" style={{ color: "#f97316" }}>Se connecter</span></Link>
          </>
        ) : (
          <div className="text-center">
            <MailOpen size={56} color="#f97316" className="mx-auto my-4" />
            <p className="text-sm mb-5" style={{ color: "#666" }}>Entrez le code à 6 chiffres envoyé à votre adresse email</p>
            <input data-testid="register-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric" maxLength={6} placeholder="000000"
              className="w-52 mx-auto text-center outline-none rounded-xl py-3 mb-5"
              style={{ fontSize: 32, fontWeight: 800, letterSpacing: 8, background: "#f8f9fa", border: "2px solid #f97316", color: "#1a1a1a" }} />
            <button onClick={verifyAndRegister} disabled={busy} data-testid="register-verify"
              className="w-full rounded-2xl flex items-center justify-center gap-2 font-bold text-white py-3.5" style={{ background: "linear-gradient(90deg,#22c55e,#16a34a)" }}>
              <CheckCircle size={20} /> {busy ? "Création..." : "Valider et créer mon compte"}
            </button>
            <button onClick={sendCode} className="text-sm mt-4 font-semibold" style={{ color: "#f97316" }}>Renvoyer le code</button>
          </div>
        )}
      </div>
    </div>
  );
}
